import React, { forwardRef, ReactNode } from "react";
import Avatar, { AvatarProps } from "../avatar/Avatar";
import Box, { BoxProps } from "../box/Box";
import SvgBox from "../box/SvgBox";
import { Tooltip } from "../tooltip";

export interface ITagProps extends BoxProps {
  children: ReactNode;
  active?: boolean;
  selected?: boolean;
  variant?: "regular" | "rounded";
}

const Tag = forwardRef<any, ITagProps>(function Tag(
  { children, active = false, selected = false, title, variant, ...props },
  ref,
) {
  const tag = (
    <Box
      py={1}
      px={2}
      ref={ref}
      fontSize="xs"
      minHeight="24px"
      bg="white"
      textTransform="uppercase"
      color="gray.600"
      maxWidth={["280px", "340px", "400px"]}
      as="span"
      display="inline-flex"
      alignItems="center"
      {...applyTagStyles({ active, selected, variant })}
      {...props}
    >
      {children}
    </Box>
  );

  if (title) {
    return (
      <Tooltip
        aria-label={title}
        label={title}
        fontSize="sm"
        bg="blue.600"
        color="white"
        px={1}
        rounded="sm"
        showDelay={500}
      >
        {tag}
      </Tooltip>
    );
  }

  return tag;
});

export default Tag;

export function TagAvatar({ ...props }: AvatarProps) {
  return (
    <Box as="span" mr={1}>
      <Avatar size="2xs" {...props} />
    </Box>
  );
}

interface ITagTextProps {
  children: ReactNode;
}
export function TagText({ children }: ITagTextProps) {
  return (
    <Box as="span" isTruncated={true}>
      {children}
    </Box>
  );
}

export function applyTagStyles(props: Pick<ITagProps, "active" | "selected" | "variant">): BoxProps {
  if (props.variant === "rounded") {
    if (props.selected) {
      return {
        rounded: "xl",
        border: "1px",
        borderColor: "teal.600",
      };
    }

    return {
      rounded: "xl",
      border: "1px",
      borderColor: "gray.200",
    };
  }

  if (props.active) {
    return {
      rounded: "sm",
      border: "2px",
      borderColor: "cyan.600",
    };
  }

  if (props.selected) {
    return {
      rounded: "sm",
      border: "1px",
      borderColor: "cyan.600",
    };
  }

  return {
    rounded: "sm",
    border: "1px",
    borderColor: "gray.200",
  };
}

interface ITagCloseButtonProps {
  disabled?: boolean;
}

export const TagCloseButton: React.FC<ITagCloseButtonProps & BoxProps> = ({
  disabled,
  ...props
}: ITagCloseButtonProps & BoxProps) => (
  <Box
    as="button"
    display="flex"
    alignItems="center"
    justifyContent="center"
    transition="all 0.2s"
    size="1.25rem"
    outline="none"
    mr={-1}
    ml={1}
    opacity={1}
    // @ts-ignore
    type="button"
    // @ts-ignore
    disabled={disabled}
    _disabled={{
      opacity: 0.4,
      cursor: "not-allowed",
      boxShadow: "none",
    }}
    _focus={{
      color: "cyan.700",
    }}
    _hover={{
      color: "cyan.700",
    }}
    color="cyan.600"
    {...props}
  >
    <SvgBox viewBox="0 0 16 16" size="18px">
      <path
        d="M9.41 8l2.29-2.29c.19-.18.3-.43.3-.71a1.003 1.003 0 0 0-1.71-.71L8 6.59l-2.29-2.3a1.003 1.003 0 0 0-1.42 1.42L6.59 8 4.3 10.29c-.19.18-.3.43-.3.71a1.003 1.003 0 0 0 1.71.71L8 9.41l2.29 2.29c.18.19.43.3.71.3a1.003 1.003 0 0 0 .71-1.71L9.41 8z"
        fillRule="evenodd"
        fill="currentColor"
      />
    </SvgBox>
  </Box>
);
